import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { PlayerRole, getLanguage, formatString } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';
import { getRoleConfig } from '../../utils/roleConfig';

export const UndercoverVoting: React.FC = () => {
  const { socket, gameState, myRole, currentRoom, myPlayerId } = useSocket();
  const t = useTranslations();
  const [tempSelection, setTempSelection] = React.useState<string | null>(null);
  const [lang, setLang] = React.useState<'en' | 'zh'>(getLanguage());
  
  // Listen for language changes
  React.useEffect(() => {
    const handleLanguageChange = () => {
      setLang(getLanguage());
    };
    window.addEventListener('languagechange', handleLanguageChange);
    return () => window.removeEventListener('languagechange', handleLanguageChange);
  }, []);
  
  // Get my vote (only visible to me)
  const myVote = gameState && myPlayerId && currentRoom?.phase === 'voting' 
    ? gameState.votes[myPlayerId] 
    : undefined;
  const hasVoted = myVote != null;
  
  // Get player's word based on role
  const isBlank = myRole === PlayerRole.BLANK;
  let myWord = '';
  if (gameState && myRole === PlayerRole.CIVILIAN) {
    myWord = lang === 'zh' 
      ? (gameState.civilianWordZh || gameState.civilianWord || '')
      : (gameState.civilianWord || gameState.civilianWordZh || '');
  } else if (gameState && myRole === PlayerRole.UNDERCOVER) {
    myWord = lang === 'zh'
      ? (gameState.undercoverWordZh || gameState.undercoverWord || '')
      : (gameState.undercoverWord || gameState.undercoverWordZh || '');
  }
  
  // Get round-specific hint for blank players (if available)
  const blankRoundHint = gameState
    ? (lang === 'zh'
        ? (gameState.blankHintZh || gameState.blankHint || '').trim()
        : (gameState.blankHint || gameState.blankHintZh || '').trim())
    : '';
  
  React.useEffect(() => {
    // Reset when entering voting phase or when vote is cleared
    if (currentRoom?.phase === 'voting') {
      if (myVote) {
        setTempSelection(myVote);
      } else {
        setTempSelection(null);
      }
    } else {
      setTempSelection(null);
    }
  }, [myVote, currentRoom?.phase]);

  if (!gameState || !currentRoom) {
    return null;
  }

  const isWordsmith = myRole === PlayerRole.WORDSMITH;
  const eliminatedPlayers = gameState.eliminatedPlayers || [];
  const isEliminated = myPlayerId ? eliminatedPlayers.includes(myPlayerId) : false;
  const isTie = gameState.isTie || false;
  const gameResult = gameState.gameResult;
  const isGhostMode = currentRoom.undercoverConfig?.mode === 'ghost';

  // Get all votable players (excluding wordsmith and eliminated players)
  // Include disconnected players so they can still be voted
  const allVotablePlayers = currentRoom.players.filter(
    (p) =>
      !p.isSpectator &&
      p.role !== PlayerRole.WORDSMITH &&
      !eliminatedPlayers.includes(p.id),
  );
  
  // Sort players according to votingOrder if available, otherwise use default order
  const votingOrder = gameState.votingOrder || [];
  
  // Use votingOrder if available - strictly follow the order from server
  const votablePlayers = React.useMemo(() => {
    if (votingOrder.length > 0) {
      const ordered = votingOrder
        .map(playerId => allVotablePlayers.find(p => p.id === playerId))
        .filter((p): p is NonNullable<typeof p> => p !== undefined);
      const notInVotingOrder = allVotablePlayers.filter(p => !votingOrder.includes(p.id));
      return [...ordered, ...notInVotingOrder];
    }
    return [...allVotablePlayers].sort((a, b) => {
      const indexA = currentRoom.players.findIndex(p => p.id === a.id);
      const indexB = currentRoom.players.findIndex(p => p.id === b.id);
      return indexA - indexB;
    });
  }, [votingOrder, allVotablePlayers, currentRoom.players]);

  // Get all players who have voted
  // All players (including disconnected) must submit votes
  // Disconnected players need to reconnect and vote before voting can complete
  const votedPlayerIds = Object.keys(gameState.votes);
  const allVoted = votablePlayers.length > 0 && votablePlayers.every(p => votedPlayerIds.includes(p.id));

  const roleConfig = getRoleConfig(t);

  const handleSelect = (playerId: string) => {
    if (!hasVoted && !isWordsmith && !isEliminated) {
      setTempSelection(playerId);
    }
  };

  const handleConfirmVote = () => {
    if (!socket || !tempSelection || hasVoted || isWordsmith || isEliminated) return;
    socket.emit('client:submit-vote', tempSelection);
  };

  // Show game result if game ended
  if (gameResult && currentRoom.phase === 'results') {
    let resultText = '';
    let resultColor = '';
    
    if (gameResult === 'civilian_win') {
      resultText = isGhostMode ? t.games.undercover.ghostResultCivilianWinVoting : t.games.undercover.civilianWin;
      resultColor = 'from-green-600 to-emerald-600';
    } else if (gameResult === 'undercover_win') {
      resultText = isGhostMode
        ? (gameState.ghostTie
            ? t.games.undercover.ghostResultUndercoverWinTie
            : t.games.undercover.ghostResultUndercoverWinSurvived)
        : t.games.undercover.undercoverWin;
      resultColor = 'from-orange-600 to-amber-600';
    } else if (gameResult === 'blank_win') {
      resultText = t.games.undercover.blankWin;
      resultColor = 'from-purple-600 to-violet-600';
    } else if (gameResult === 'tie') {
      resultText = t.games.undercover.tie;
      resultColor = 'from-gray-600 to-slate-600';
    }

    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
        <div className="w-full text-center mb-6">
          <h2 className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${resultColor} bg-clip-text text-transparent mb-4`}>
            {resultText}
          </h2>
          <p className="text-base sm:text-lg text-gray-700">{t.games.undercover.allRolesRevealed}</p>
        </div>

        {isGhostMode && (
          <div className="w-full mb-6 p-4 rounded-xl border-2 border-orange-200 bg-orange-50">
            <p className="text-sm text-orange-900 font-semibold text-center mb-2">
              {t.games.undercover.ghostRoundFactReview}
            </p>
            {(gameState.ghostFactOrder || []).map((playerId) => {
              const player = currentRoom.players.find((p) => p.id === playerId);
              const fact = gameState.ghostFacts?.[playerId] || '（未提交）';
              return (
                <p key={playerId} className="text-sm text-orange-900">
                  {player?.name || t.games.undercover.unknownPlayer}：{fact}
                </p>
              );
            })}
          </div>
        )}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentRoom.players
            .filter(p => p.role !== PlayerRole.WORDSMITH)
            .map((player) => {
              if (!player.role) return null;
              const config = roleConfig[player.role];
              const isEliminated = isGhostMode ? false : eliminatedPlayers.includes(player.id);
              
              return (
                <div
                  key={player.id}
                  className={`p-4 sm:p-6 rounded-xl border-2 bg-gradient-to-r ${config.gradient} text-white ${
                    isEliminated ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold">{player.name}</h3>
                      {isEliminated && (
                        <p className="text-xs sm:text-sm opacity-75 mt-1">{t.games.undercover.eliminated}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xl sm:text-2xl">{config.emoji}</span>
                        <p className="text-sm sm:text-base opacity-90">{config.name}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  }

  const roundSummary = React.useMemo(() => {
    const details = gameState.lastRoundVoteDetails;
    if (!details || Object.keys(details).length === 0) {
      return null;
    }

    const playerNameMap = new Map(currentRoom.players.map(player => [player.id, player.name]));
    const playerOrderMap = new Map(currentRoom.players.map((player, index) => [player.id, index]));
    const votesByTarget: Record<string, string[]> = {};

    Object.entries(details).forEach(([voterId, targetId]) => {
      const voterName = playerNameMap.get(voterId);
      const targetName = playerNameMap.get(targetId);
      if (!voterName || !targetName) return;

      if (!votesByTarget[targetId]) {
        votesByTarget[targetId] = [];
      }
      votesByTarget[targetId].push(voterId);
    });

    const separator = lang === 'zh' ? '，' : ', ';
    const lines = Object.entries(votesByTarget)
      .sort(([targetA], [targetB]) => (playerOrderMap.get(targetA) ?? 999) - (playerOrderMap.get(targetB) ?? 999))
      .map(([targetId, voterIds]) => {
        const targetName = playerNameMap.get(targetId);
        if (!targetName) return null;
        const votersText = [...voterIds]
          .sort((a, b) => (playerOrderMap.get(a) ?? 999) - (playerOrderMap.get(b) ?? 999))
          .map(voterId => playerNameMap.get(voterId) || voterId)
          .join(separator);
        return formatString(t.games.undercover.votedFor, {
          voters: votersText,
          target: targetName,
        });
      })
      .filter((line): line is string => Boolean(line));

    let message = '';
    if (isTie) {
      message = t.games.undercover.tieContinueNextRound;
    } else if (!gameResult && eliminatedPlayers.length > 0) {
      const lastEliminated = eliminatedPlayers[eliminatedPlayers.length - 1];
      const eliminatedPlayer = currentRoom.players.find(p => p.id === lastEliminated);
      message = formatString(t.games.undercover.eliminatedContinueNextRound, {
        player: eliminatedPlayer?.name || t.games.undercover.unknownPlayer,
      });
    }

    return {
      message,
      lines,
    };
  }, [gameState.lastRoundVoteDetails, votablePlayers, lang, t.games.undercover, isTie, gameResult, eliminatedPlayers, currentRoom.players]);

  // Show voting interface
  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <div className="w-full text-center mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2 px-2">
          {t.games.undercover.voting}
        </h2>
        {isEliminated ? (
          <p className="text-base sm:text-lg text-gray-700 mb-2 sm:mb-4 px-2">{t.games.undercover.youAreEliminated}</p>
        ) : isWordsmith ? (
          <p className="text-base sm:text-lg text-gray-700 mb-2 sm:mb-4 px-2">{t.games.undercover.cannotVoteAsWordsmith}</p>
        ) : hasVoted ? (
          <p className="text-base sm:text-lg text-gray-700 mb-2 sm:mb-4 px-2">{t.games.undercover.alreadyVoted}</p>
        ) : (
          <p className="text-base sm:text-lg text-gray-700 mb-2 sm:mb-4 px-2">
            {isGhostMode ? t.games.undercover.ghostSelectUndercoverHint : t.games.undercover.selectUndercover}
          </p>
        )}
        {!allVoted && !isWordsmith && !isEliminated && (
          <p className="text-xs sm:text-sm text-gray-500 px-2">
            {t.games.undercover.voted}: {votedPlayerIds.length} / {votablePlayers.length}
          </p>
        )}
      </div>

      {isGhostMode && !isWordsmith && !isEliminated && (
        <div
          className={`w-full mb-4 p-3 rounded-xl text-center ${
            myRole === PlayerRole.CIVILIAN
              ? 'border-2 border-amber-200 bg-gradient-to-br from-yellow-50 to-orange-50'
              : myRole === PlayerRole.UNDERCOVER
                ? 'border-2 border-rose-200 bg-gradient-to-br from-red-50 to-rose-50'
                : 'border-2 border-slate-200 bg-slate-50'
          }`}
        >
          <p
            className={`text-xs ${
              myRole === PlayerRole.CIVILIAN
                ? 'text-amber-900'
                : myRole === PlayerRole.UNDERCOVER
                  ? 'text-rose-900'
                  : 'text-slate-700'
            }`}
          >
            {t.games.undercover.ghostYourRolePrivate}
          </p>
          <p
            className={`text-base font-bold mt-1 ${
              myRole === PlayerRole.CIVILIAN
                ? 'text-amber-950'
                : myRole === PlayerRole.UNDERCOVER
                  ? 'text-rose-950'
                  : 'text-slate-900'
            }`}
          >
            {myRole === PlayerRole.UNDERCOVER
              ? t.roles.undercover
              : myRole === PlayerRole.CIVILIAN
                ? t.roles.civilian
                : '—'}
          </p>
        </div>
      )}

      {/* Show player's word */}
      {!isGhostMode && !isWordsmith && !isEliminated && (
        <div className="w-full bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-indigo-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-3 flex items-center gap-2">
            <span className="text-lg sm:text-xl">📖</span>
            {t.games.undercover.yourWordIs}
          </h3>
          {myWord ? (
            <div className="w-full overflow-x-auto">
              <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 text-center whitespace-nowrap">
                {myWord}
              </p>
            </div>
          ) : isBlank ? (
            <div>
              <p className="text-lg sm:text-xl font-semibold text-gray-600 text-center mb-3">
                ( _ )
              </p>
              {(gameState?.blankHint || gameState?.blankHintZh) && (
                <div className="mt-3 p-3 sm:p-4 rounded-lg bg-yellow-50 text-yellow-900 border border-yellow-200">
                  <p className="text-xs sm:text-sm font-semibold mb-1">
                    {lang === 'zh' ? '提示' : 'Hint'}
                  </p>
                  <p className="text-sm sm:text-base">
                    {blankRoundHint || (lang === 'zh' ? gameState?.blankHintZh : gameState?.blankHint) || (lang === 'zh' ? gameState?.blankHint : gameState?.blankHintZh)}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-base sm:text-lg text-gray-500 text-center">
              {t.games.undercover.loading}
            </p>
          )}
        </div>
      )}

      {/* Instructions / last round summary */}
      {!isGhostMode && (roundSummary || (!isWordsmith && !hasVoted && !isEliminated)) && (
        <div className="w-full bg-blue-50 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 border-2 border-blue-200">
          {roundSummary && roundSummary.lines.length > 0 && (
            <div className="mt-2 space-y-1">
              {roundSummary.lines.map((line, index) => (
                <p key={`${line}-${index}`} className="text-sm sm:text-base text-blue-900 text-center">
                  {line}
                </p>
              ))}
            </div>
          )}
          <p className={`text-sm sm:text-base text-blue-900 text-center font-semibold ${roundSummary?.lines.length ? 'mt-2' : ''}`}>
            {roundSummary?.message || t.games.undercover.describeWordInOrder}
          </p>
        </div>
      )}

      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        {votablePlayers.map((player) => {
          const isSelected = !isWordsmith && !isEliminated && !hasVoted && tempSelection === player.id;
          const isMyVote = myVote === player.id;
          const ghostFact =
            isGhostMode &&
            (gameState.ghostFacts?.[player.id]?.trim() || t.games.undercover.ghostFactNotSubmitted);

          const Component = !isWordsmith && !isEliminated && !hasVoted ? 'button' : 'div';
          const componentProps = !isWordsmith && !isEliminated && !hasVoted 
            ? {
                onClick: () => handleSelect(player.id),
              }
            : {};
          
          return (
            <Component
              key={player.id}
              {...componentProps}
              className={`p-4 sm:p-6 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-orange-500 bg-orange-50 shadow-lg'
                  : isMyVote
                  ? 'border-green-500 bg-green-50 shadow-md'
                  : hasVoted || isWordsmith || isEliminated
                  ? 'border-gray-200 bg-gray-50 opacity-60'
                  : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1 text-left">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800">{player.name}</h3>
                  {ghostFact && (
                    <p className="text-sm sm:text-base text-orange-900/90 mt-2 break-words leading-snug">
                      {ghostFact}
                    </p>
                  )}
                  {isMyVote && (
                    <p className="text-xs sm:text-sm text-green-600 mt-1">{t.games.undercover.voted}</p>
                  )}
                </div>
                {isSelected && (
                  <span className="text-xl sm:text-2xl">✓</span>
                )}
              </div>
            </Component>
          );
        })}
      </div>

      {!isWordsmith && !isEliminated && !hasVoted && tempSelection && (
        <div className="w-full mb-4 sm:mb-6">
          <button
            onClick={handleConfirmVote}
            className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg hover:from-orange-700 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            {t.games.undercover.confirmVote}
          </button>
        </div>
      )}

    </div>
  );
};
